import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HireTableView from './HireTableView';
import OffHireAllDialog from './OffHireAllDialog';
import ExtendAllDialog from './ExtendAllDialog';
import ReHireAllDialog from './ReHireAllDialog';
import HireAdjustmentDecisionDialog from './HireAdjustmentDecisionDialog';
import { arrangeHireOrderMenu } from './hireOrderMenu';
import useDeptOrderExpansion from '../../hooks/useDeptOrderExpansion';
import useMyOrderExpansion from '../../hooks/useMyOrderExpansion';
import useUnsavedChangesGuard, { UNSAVED_ORDER_CHANGES_MESSAGE } from '../../hooks/useUnsavedChangesGuard';
import { useDepartment } from '../../contexts/DepartmentContext';
import { useToastOrNoop } from '../../contexts/ToastContext';
import { apiGet, apiPost, API_BASE } from '../../utils/api';
import { isHireOrderCancelled } from '../../utils/hireBuckets';
import { patchHireWithAdjustmentDecision } from '../../utils/hirePatchWithAdjustmentDecision';
import useAdjustmentDecision from '../../hooks/useAdjustmentDecision';
import { formatDate } from '../../utils/dateFormat';

/**
 * The hires TABLE, with each row expanding into its ORDER — the SAME expanded
 * panel Department Orders / My Orders render (2026-09-27). Owner: *"it should
 * be fairly simple to use the exact same expansion as all of the edit/access
 * rights should work as Hires are just orders"*, and *"no need to duplicate
 * much… Its only a differnt page 'Hires' with differnt named columns and
 * tabs"*.
 *
 * ── Which order surface, and why it is the one the page would link to ──
 * - `DeptHireOrderTable` — Department Hires, and My Hires for a viewer who
 *   `canViewDeptWide`: runs `hooks/useDeptOrderExpansion`, fed by
 *   `GET /api/orders/department?ids=<order_id>` — the Department Orders list
 *   row for exactly that order (the `?ids=` narrowing ANDs onto the server's
 *   own scope; every derived field is identical to the unfiltered list's).
 * - `OwnHireOrderTable` — My Hires otherwise: runs `hooks/useMyOrderExpansion`,
 *   fed by My Orders' own `GET /api/orders?user_id=`, narrowed CLIENT-side to
 *   the one order.
 * Those are the two destinations the pages' "View order" links already pick
 * between, so a row expands into the surface the link would have opened —
 * WHEN that surface lists the order. It may not: GET /api/hires is
 * department-wide for canViewDepartmentOrders; GET /api/orders/department
 * narrows a sub-department lead to their sub-department + chain, so a listed
 * hire can have no expandable row. That ABSENCE (a 200 with no row) is not a
 * failure and is told apart from one: it renders `ORDER_NOT_LISTED_MESSAGE`,
 * and only a THROWN fetch renders the shared "Failed to load order details."
 * line. The server is right to narrow — never widen it to open the panel.
 *
 * 🚨 NOTHING IS RE-DERIVED HERE. The hooks are the order pages' own; every
 * permission is the server's `detail.can_edit` / `can_action` and
 * `useOrderAuthority().canManageOrder`, where Orders reads them.
 *
 * ── What this file owns (the LIST concerns the hooks leave to a caller) ──
 * - the ONE order row (`orders`) the panel needs and its fetch;
 * - `fetchOrders`, which refreshes that row AND the hires list
 *   (`onHiresChanged`) — a save, a received tick and the bulk hire dialogs
 *   land here;
 * - 🔻 CORRECTED 2026-09-29 — "every save, action and tick lands here" was
 *   FALSE: the hooks' row ACTIONS (Mark as Complete, Re-open, approve, status
 *   jump …) go through `refreshOrderRow`, which calls `fetchOrders` only when
 *   the per-order refresh FAILS, and an attachment never called it at all. So
 *   the tabs, counts and the paperclip sat stale until a reload (owner's
 *   staging feedback). Both hooks now take `onOrderMutated`, fired on EVERY
 *   order-row write and every attachment change; this file passes the page's
 *   silent refetch. Pinned by `HireOrderExpansion.listRefresh.test.js`;
 * - WHICH hire line shows the panel (`expandedItemId`): the hooks key the
 *   expansion by ORDER, and one order can carry several hire lines — only
 *   the clicked one opens (two open copies of one order's form would collide
 *   on `OrderForm`'s hard-coded ids);
 * - the leave guard and the department-switch dirty registration, as each
 *   order page does for its own inline editor.
 *
 * 💤 `deferReferenceData`: the hooks' reference lists load on the FIRST
 * expand, not when the hires page mounts — most visits never expand a row.
 */

/** The panel's line for a listed hire whose order is not on this viewer's orders list. */
export const ORDER_NOT_LISTED_MESSAGE = "This order isn't in your orders list, so it can't be opened here.";

/**
 * The one order row the expanded panel needs, fetched when the expanded
 * order changes. `status` is `{ id, state: 'loading' | 'done' | 'absent' | 'failed' }`
 * — `absent`: the fetch answered and the order is not in it; `failed`: it threw —
 * for the LAST fetch; a silent refresh keeps the row on screen meanwhile.
 */
function useExpandedOrderRow(fetchRows) {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState({ id: null, state: 'done' });
  const orderIdRef = useRef(null);

  const fetchOrderRow = useCallback(async () => {
    const id = orderIdRef.current;
    if (id == null) return;
    setStatus((prev) => (prev.id === id && prev.state === 'done' ? prev : { id, state: 'loading' }));
    try {
      const rows = await fetchRows(id);
      if (orderIdRef.current !== id) return; // superseded by another expand
      const row = rows.find((o) => o.id === id);
      setOrders((prev) => [...prev.filter((o) => o.id !== id), ...(row ? [row] : [])]);
      setStatus({ id, state: row ? 'done' : 'absent' });
    } catch {
      if (orderIdRef.current !== id) return;
      // Fail to EMPTY, never to STALE — the `refreshOneOrder` rule.
      setOrders((prev) => prev.filter((o) => o.id !== id));
      setStatus({ id, state: 'failed' });
    }
  }, [fetchRows]);

  return { orders, setOrders, status, orderIdRef, fetchOrderRow };
}

/**
 * The hire-row side of an order-expansion hook: which line is open, the row
 * toggle, and the panel for a line. `exp` is the hook's return value.
 */
function useHireRowExpansion({
  exp, editDirtyId, orders, status, orderIdRef, fetchOrderRow, expandItem, onExpandHandled, onDirtyItemChange,
  continueWhenCleanRef, onExpandedItemChange,
}) {
  const [expandedItemId, setExpandedItemId] = useState(null);

  // The page's VIEW switch, status tabs and "View order" unmount this table's
  // form: each goes through the hook's own `continueWhenClean` (the three-way
  // prompt, then the click — 2026-09-28; was refuse + pulse), never a second
  // dirty test.
  useEffect(() => {
    if (!continueWhenCleanRef) return undefined;
    continueWhenCleanRef.current = exp.continueWhenClean;
    return () => { continueWhenCleanRef.current = null; };
  }, [continueWhenCleanRef, exp.continueWhenClean]);
  const expandedOrderId = exp.expandedOrders.size > 0 ? [...exp.expandedOrders][0] : null;

  // The row the panel needs follows the ORDER the hook has open.
  useEffect(() => {
    orderIdRef.current = expandedOrderId;
    if (expandedOrderId != null) fetchOrderRow();
  }, [expandedOrderId, orderIdRef, fetchOrderRow]);

  // The held click RE-ENTERS the latest `onToggle` once the edit is saved or
  // discarded, so it runs against the collapsed, clean state.
  const onToggleRef = useRef(null);
  const onToggle = useCallback((item, e) => {
    const orderId = item.order_id;
    const open = exp.expandedOrders.has(orderId);
    const ownLine = open && expandedItemId === item.item_id;
    if (editDirtyId !== null && !ownLine) {
      // Another hire row, or another LINE of the dirty order (moving the panel
      // remounts the form): the hook's three-way prompt, then this click.
      if (e) e.stopPropagation();
      exp.continueWhenClean(() => onToggleRef.current(item));
      return;
    }
    if (open && !ownLine) {
      // Another LINE of the (clean) open order: move the panel.
      if (e) e.stopPropagation();
      setExpandedItemId(item.item_id);
      return;
    }
    // Open / collapse through the hook: collapsing the dirty row's own line
    // asks through the hook, never re-implemented here.
    exp.toggleExpand(orderId, e);
    if (!open) setExpandedItemId(item.item_id);
  }, [exp, editDirtyId, expandedItemId]);
  onToggleRef.current = onToggle;

  const renderPanel = useCallback((item) => {
    const orderId = item.order_id;
    const order = orders.find((o) => o.id === orderId);
    const detail = exp.orderDetails[orderId];
    const rowSettled = status.id === orderId && status.state !== 'loading';
    if (!order && rowSettled && status.state === 'absent') {
      return {
        isLoading: false,
        detail: true, // settled — the panel renders `content`, not its failure line
        content: (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid={`hire-row-not-listed-${item.item_id}`}>
            {ORDER_NOT_LISTED_MESSAGE}
          </p>
        ),
      };
    }
    const isLoading = !!exp.detailsLoading[orderId] || (!order && !rowSettled);
    return {
      isLoading,
      detail: order && detail ? detail : null,
      content: order && detail ? exp.renderExpanded(order) : null,
    };
  }, [exp, orders, status]);

  const openItemId = expandedOrderId != null ? expandedItemId : null;

  // The page remembers the open hire across views (`useHiresViewSwitch`).
  // Reported on a CHANGE only — never the mount's `null` (a return to the
  // Table must not forget the hire it is about to re-open) and never on
  // unmount (leaving the Table is not closing the hire).
  const reportedItemRef = useRef(openItemId);
  useEffect(() => {
    if (!onExpandedItemChange || reportedItemRef.current === openItemId) return;
    reportedItemRef.current = openItemId;
    onExpandedItemChange(openItemId);
  }, [openItemId, onExpandedItemChange]);

  // The page's `?hire=` deep link asks for a line to be opened: the SAME
  // `onToggle` a click uses (so a dirty form elsewhere still refuses), once.
  useEffect(() => {
    if (!expandItem) return;
    if (openItemId !== expandItem.item_id) onToggle(expandItem);
    if (onExpandHandled) onExpandHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandItem]);

  // The page pins the hire line whose order form is DIRTY (paging, tabs,
  // the filter box and refetches must not slice it away) and pauses its poll — so it
  // needs the LINE, not just the order the hooks key by.
  const dirtyItemId = editDirtyId !== null ? openItemId : null;
  useEffect(() => {
    if (onDirtyItemChange) onDirtyItemChange(dirtyItemId);
  }, [dirtyItemId, onDirtyItemChange]);
  useEffect(() => () => { if (onDirtyItemChange) onDirtyItemChange(null); }, [onDirtyItemChange]);

  return { expandedItemId: openItemId, onToggle, renderPanel };
}

/** The tooltip on a hire action while the expanded order form has unsaved edits. */
export const HIRE_ACTION_SAVE_FIRST = 'Save or discard your changes first';

/**
 * THE HIRE ACTIONS INSIDE AN EXPANDED ORDER (2026-09-28). Owner: *"I dont think
 * we should have an action menu until you expand the hire. This would keep
 * consistancy with orders AND save a whole column"*, then *"the main header
 * action menu to have off hire all, then each line item has the action menu
 * like Dep Orders to offhire each line item"*.
 *
 * Returns `hireActions` for `useDeptOrderExpansion` / `useMyOrderExpansion`:
 *   - `arrangeMenu(order, { dirty }, menuProps)` — the order's `Actions ▾` in the
 *     hires layout (2026-09-28 ruling, `./hireOrderMenu.js`): Off hire all +
 *     Extend all… while a line is out, Bring all back on hire once none is;
 *   - `lineItems(order, item)` — Extend / Off hire, or Bring back on hire, on
 *     that hire line's own item menu.
 * 🔑 GATE: the page's `canManage` — THE SAME boolean the Kanban cards (and,
 * until 2026-09-28, the Table's now-deleted row buttons) read, never a new
 * one; the server gates every call independently. HANDLERS: the page's own
 * `onExtend` / `onOffHire` / `onReHire` (its modals), called with the
 * HIRE-LIST row for that line (`list.allItems`), exactly as the cards call
 * them — nothing is reimplemented here. While the order form is
 * DIRTY every item is disabled with `HIRE_ACTION_SAVE_FIRST` (the
 * unsaved-changes popup is a later round).
 * ⚖️ Offered on the HIRES pages' expansion only: Department Orders / My
 * Orders pass no `hireActions` and render byte-identically.
 * 🔻 2026-09-28: a hire whose order is cancelled/rejected offers NEITHER —
 * `isHireOrderCancelled` (`utils/hireBuckets.js`), checked in both `arrangeMenu`
 * and `lineItems`. It was never really hired, so there is nothing to extend,
 * off-hire or bring back.
 */
function useHireLineActions({ canManage, onExtend, onOffHire, onReHire, onViewOrder, allItems }) {
  // Each is { order, lines } while its dialog is open.
  const [offHireAll, setOffHireAll] = useState(null);
  const [extendAll, setExtendAll] = useState(null);
  const [reHireAll, setReHireAll] = useState(null);
  // Ref-held: the pages pass a fresh arrow each render, which must not
  // re-mint `hireActions`.
  const viewOrderRef = useRef(onViewOrder);
  useEffect(() => { viewOrderRef.current = onViewOrder; });
  const hireActions = useMemo(() => {
    const rows = Array.isArray(allItems) ? allItems : [];
    const guarded = (dirty, title) => (dirty ? { disabled: true, title: HIRE_ACTION_SAVE_FIRST } : { title });
    // A cancelled/rejected order's hires were never really hired — no hire
    // action on them (owner ruling 2026-09-28, `utils/hireBuckets.js`).
    const liveRowsOf = (order) => rows.filter((r) => r.order_id === order.id && !isHireOrderCancelled(r));
    return {
      /**
       * The order's `Actions ▾` on a hires page — see `./hireOrderMenu.js`
       * for the ruled layout. The Hire section holds Off hire all + Extend
       * all while ANY line is still out, and Bring all back on hire once
       * EVERY line is off hired (never both: the mixed state is the on-hire
       * state).
       */
      arrangeMenu(order, { dirty } = {}, props) {
        const live = liveRowsOf(order);
        const onHire = live.filter((r) => r.hire_status !== 'off_hired');
        const offHired = live.filter((r) => r.hire_status === 'off_hired');
        const hireItems = [];
        if (canManage && onHire.length > 0) {
          hireItems.push({
            key: 'off-hire-all',
            label: `Off hire all (${onHire.length} ${onHire.length === 1 ? 'line' : 'lines'})`,
            tone: 'deliver',
            testId: `hire-menu-off-hire-all-${order.id}`,
            ...guarded(dirty, `Mark every hire line on this order as returned (${onHire.length})`),
            onClick: () => setOffHireAll({ order, lines: onHire }),
          }, {
            key: 'extend-all',
            label: 'Extend all…',
            tone: 'approve',
            testId: `hire-menu-extend-all-${order.id}`,
            // Owner, 2026-09-29, of the old wording: "doesn't make sense".
            ...guarded(dirty, 'Update the off-hire day for all items still on hire'),
            onClick: () => setExtendAll({ order, lines: onHire }),
          });
        } else if (canManage && offHired.length > 0) {
          hireItems.push({
            key: 'rehire-all',
            label: 'Bring all back on hire',
            tone: 'nav',
            testId: `hire-menu-rehire-all-${order.id}`,
            ...guarded(dirty, `Put every returned line back on hire (${offHired.length})`),
            onClick: () => setReHireAll({ order, lines: offHired }),
          });
        }
        return arrangeHireOrderMenu(props, {
          hireItems,
          allOffHired: onHire.length === 0,
          // The page's own `handleViewOrder` (behind its continueWhenClean).
          viewOrder: viewOrderRef.current ? () => viewOrderRef.current(order.id) : null,
        });
      },
      lineItems(order, item, { dirty } = {}) {
        if (!canManage || !item || item.id == null) return [];
        const row = rows.find((r) => r.item_id === item.id);
        if (!row || isHireOrderCancelled(row)) return [];
        if (row.hire_status !== 'off_hired') {
          return [
            // A legacy line with no last day (`hire_to` NULL) has nothing to
            // "extend": the same action and route SETS one (owner ruling
            // 2026-09-28, "require an end date").
            {
              key: 'hire-extend', label: row.hire_to ? 'Extend hire' : 'Set end date', tone: 'approve',
              testId: `hire-item-extend-${row.item_id}`,
              ...guarded(dirty, row.hire_to ? 'Move the last day of this hire later' : 'This hire has no last day yet — set one'),
              onClick: () => onExtend(row),
            },
            {
              key: 'hire-off-hire', label: 'Off hire', tone: 'neutral',
              testId: `hire-item-offhire-${row.item_id}`,
              ...guarded(dirty, 'Mark this hire line as returned'),
              onClick: () => onOffHire(row),
            },
          ];
        }
        return onReHire ? [{
          key: 'hire-rehire', label: 'Bring back on hire', tone: 'nav',
          testId: `hire-item-rehire-${row.item_id}`,
          ...guarded(dirty, 'Put this returned line back on hire'),
          onClick: () => onReHire(row),
        }] : [];
      },
      /**
       * THE PER-LINE "Off hired" TICK (owner's staging feedback, 2026-09-29),
       * beside "Delivered" in the line's header. Owner ruling: ticking OPENS
       * the existing single-line Off hire dialog (the page's `onOffHire` —
       * `HireActionModal` + `OffHirePriceChoice`), and the box shows ticked
       * only once that is confirmed, because `checked` READS the hire's own
       * status (never local state); unticking opens the existing "Bring Back
       * On Hire" dialog (`onReHire` → the existing re-hire route). No new
       * route, dialog or price logic. SAME gate as `lineItems`' Off hire:
       * `canManage`, a hire-list row for the line, not a cancelled/rejected
       * order. Dirty form → disabled with `HIRE_ACTION_SAVE_FIRST`.
       */
      lineTick(order, item, { dirty } = {}) {
        if (!canManage || !item || item.id == null) return null;
        const row = rows.find((r) => r.item_id === item.id);
        if (!row || isHireOrderCancelled(row)) return null;
        const offHired = row.hire_status === 'off_hired';
        const canUntick = offHired && !!onReHire;
        const base = {
          checked: offHired,
          label: 'Off hired',
          // 📱 The visible label below `md` (2026-10-01), "Received?"'s twin —
          // without it the phone row showed an anonymous second tick.
          mobileLabel: 'Off hired?',
          testId: `hire-item-offhired-tick-${row.item_id}`,
          ariaLabel: `${row.description || 'Hire line'} off hired`,
        };
        if (dirty) return { ...base, disabled: true, title: HIRE_ACTION_SAVE_FIRST, onChange: () => {} };
        if (offHired && !canUntick) return { ...base, disabled: true, title: 'Off hired', onChange: () => {} };
        return {
          ...base,
          disabled: false,
          title: offHired ? 'Off hired — untick to bring this line back on hire' : 'Tick to off hire this line (asks for the return date and price)',
          onChange: () => (offHired ? onReHire(row) : onOffHire(row)),
        };
      },
    };
  }, [canManage, onExtend, onOffHire, onReHire, allItems]);
  return { hireActions, offHireAll, setOffHireAll, extendAll, setExtendAll, reHireAll, setReHireAll };
}

/** The toast after a 409: the lines the dialog listed are no longer the lines on hire. */
export const OFF_HIRE_ALL_STALE_MESSAGE = 'These hires changed since you opened this — reopen Off hire all to see the current lines.';
/** Extend all's 409 (`extend_set_changed`). */
export const EXTEND_ALL_STALE_MESSAGE = 'These hires changed since you opened this — reopen Extend all to see the current lines.';
/** Bring all back on hire's 409 (`rehire_set_changed`). */
export const REHIRE_ALL_STALE_MESSAGE = 'These hires changed since you opened this — reopen Bring all back on hire to see the current lines.';
/** Any bulk route's 409 `order_not_live`: the order was cancelled or rejected meanwhile. */
export const HIRE_ORDER_NOT_LIVE_MESSAGE = 'This order has been cancelled or rejected, so its hires cannot be changed. Reload to see it.';

/**
 * extend-all's `adjustments_exist` body is PER LINE (`[{ item_id, adjustments: [...] }]`);
 * `HireAdjustmentDecisionDialog` takes the flat rows single-line Extend sends.
 */
const flattenAdjustments = (groups) => (Array.isArray(groups) ? groups : [])
  .flatMap((g) => (Array.isArray(g && g.adjustments) ? g.adjustments : [g]));

/**
 * ONE host for the three whole-order dialogs (Off hire all, Extend all…, Bring
 * all back on hire) and their ONE request each. On success: close, toast, and
 * refresh the order's DETAIL cache and the list (`fetchOrders`) — every
 * mutation path on an expanded row refreshes both. A 400 keeps the dialog
 * open with the server's words; the server is all-or-nothing, so nothing moved.
 * 🔀 A 409 CLOSES the dialog with a plain-words toast and refreshes: its
 * `lines` are a snapshot from the menu click, so an open dialog would re-send
 * the same stale `item_ids` for ever. Reopening builds the list afresh.
 * 💷 `askAdjustments` (Extend all): a 409 `adjustments_exist` asks keep/remove
 * through the SHARED `patchHireWithAdjustmentDecision` wrapper and the SHARED
 * `HireAdjustmentDecisionDialog` — the single-line Extend flow, never a copy —
 * and resends once with the decision. Cancel there sends nothing more and
 * leaves this dialog open, silently.
 */
function BulkHireHost({
  state, onClose, refreshOrderDetail, fetchOrders, setSuccessMessage,
  Dialog, path, staleMessage, failLabel, successMessage, askAdjustments = false,
}) {
  const toast = useToastOrNoop();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The handshake is shared (`hooks/useAdjustmentDecision.js`); extend-all's
  // 409 body is PER LINE, so it is flattened before the shared hook shows it
  // — see the docblock above `flattenAdjustments`.
  const { pendingAdjustments, onNeedsDecision: onNeedsDecisionRaw, decide } = useAdjustmentDecision();
  const onNeedsDecision = (groups) => onNeedsDecisionRaw(flattenAdjustments(groups));
  useEffect(() => { setError(''); }, [state]);
  if (!state) return null;
  const { order, lines } = state;
  const onConfirm = async (body) => {
    setBusy(true);
    setError('');
    try {
      const url = `${API_BASE}/api/hires/orders/${order.id}/${path}`;
      const res = askAdjustments
        ? await patchHireWithAdjustmentDecision(apiPost, url, body, { onNeedsDecision })
        : await apiPost(url, body);
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        if (data.error === 'adjustments_exist') return; // keep/remove cancelled — nothing sent
        onClose();
        toast.info(data.code === 'order_not_live' ? HIRE_ORDER_NOT_LIVE_MESSAGE : staleMessage);
        await Promise.all([refreshOrderDetail(order.id), fetchOrders()]);
        return;
      }
      if (!res.ok) {
        setError(data.error || `${failLabel} failed (${res.status}).`);
        return;
      }
      onClose();
      setSuccessMessage(successMessage(lines, body));
      await Promise.all([refreshOrderDetail(order.id), fetchOrders()]);
    } catch (err) {
      setError(err.message || `${failLabel} failed.`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Dialog
        order={order}
        lines={lines}
        loading={busy}
        error={error}
        onConfirm={onConfirm}
        onCancel={() => { if (!busy) onClose(); }}
      />
      {pendingAdjustments != null && (
        <HireAdjustmentDecisionDialog adjustments={pendingAdjustments} onDecide={decide} />
      )}
    </>
  );
}

const lineCount = (lines) => `${lines.length} hire ${lines.length === 1 ? 'line' : 'lines'}`;

/** The three hosts, rendered once by each table below. */
function HireBulkDialogs({ lineActions, refreshOrderDetail, fetchOrders, setSuccessMessage }) {
  const shared = { refreshOrderDetail, fetchOrders, setSuccessMessage };
  return (
    <>
      <BulkHireHost
        {...shared}
        state={lineActions.offHireAll}
        onClose={() => lineActions.setOffHireAll(null)}
        Dialog={OffHireAllDialog}
        path="off-hire-all"
        staleMessage={OFF_HIRE_ALL_STALE_MESSAGE}
        failLabel="Off hire all"
        successMessage={(lines) => `Off hired ${lineCount(lines)}.`}
      />
      <BulkHireHost
        {...shared}
        state={lineActions.extendAll}
        onClose={() => lineActions.setExtendAll(null)}
        Dialog={ExtendAllDialog}
        path="extend-all"
        staleMessage={EXTEND_ALL_STALE_MESSAGE}
        failLabel="Extend all"
        successMessage={(lines, body) => `Extended ${lineCount(lines)} to ${formatDate(body.new_hire_to)}.`}
        askAdjustments
      />
      <BulkHireHost
        {...shared}
        state={lineActions.reHireAll}
        onClose={() => lineActions.setReHireAll(null)}
        Dialog={ReHireAllDialog}
        path="re-hire-all"
        staleMessage={REHIRE_ALL_STALE_MESSAGE}
        failLabel="Bring all back on hire"
        successMessage={(lines) => `Brought ${lineCount(lines)} back on hire.`}
      />
    </>
  );
}

/**
 * A line action runs through the PAGE's modal, which refreshes the hires list
 * but knows nothing of the expanded order's DETAIL cache. So when the list's
 * rows for the OPEN order change (status, dates, price), refresh that order's
 * detail — never while its form is dirty (the actions are disabled then).
 */
function useDetailFollowsHireRows(exp, allItems, dirty) {
  const openId = exp.expandedOrders.size > 0 ? [...exp.expandedOrders][0] : null;
  const sig = openId == null ? '' : (Array.isArray(allItems) ? allItems : [])
    .filter((r) => r.order_id === openId)
    .map((r) => [r.item_id, r.hire_status, r.hire_from, r.hire_to, r.line_total, r.adjustment_total].join(':'))
    .join('|');
  const last = useRef({ id: null, sig: '' });
  useEffect(() => {
    if (openId != null && last.current.id === openId && last.current.sig !== sig && !dirty) {
      exp.refreshOrderDetail(openId);
    }
    last.current = { id: openId, sig };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, sig]);
}

/** The hooks' success line as a toast — the hires pages have no banner. */
function useSuccessToast() {
  const toast = useToastOrNoop();
  return useCallback((message) => {
    if (message) toast.success(message);
  }, [toast]);
}

async function fetchDepartmentRow(id) {
  const res = await apiGet(`${API_BASE}/api/orders/department?ids=${id}`);
  if (!res.ok) throw new Error(`Failed to load order ${id}: ${res.status}`);
  const data = await res.json();
  const rows = Array.isArray(data) ? data : [];
  if (rows.some((o) => o.id === id)) return rows;
  // 🔀 Owner 2026-10-03: a sub-department lead's OWN order can sit outside the
  // department list's narrowing (their sub-department + chains), which showed
  // "isn't in your orders list" for their own hire. Fall back to the viewer's
  // own orders — the My Orders list, already server-scoped to them, so this
  // shows nothing new. Without a `can_action` on that row the panel offers
  // fewer actions, never more.
  const userId = localStorage.getItem('sfx_user_id');
  if (!userId) return rows;
  const own = await apiGet(`${API_BASE}/api/orders?user_id=${userId}`);
  if (!own.ok) return rows;
  const ownData = await own.json();
  return Array.isArray(ownData) ? ownData.filter((o) => o.id === id) : rows;
}

/**
 * `fetchOrders` for a hook: refresh the panel's order row AND the hires list,
 * so a save, action or tick in the panel never leaves a hire row stale.
 */
function useFetchOrders(fetchOrderRow, onHiresChanged) {
  return useCallback(
    () => Promise.all([fetchOrderRow(), onHiresChanged ? onHiresChanged() : null]),
    [fetchOrderRow, onHiresChanged]
  );
}

/** Department Hires, and My Hires with the department-wide view. */
function DeptHireOrderTable({ onHiresChanged, onDirtyChange, rowRequest, tableProps }) {
  const row = useExpandedOrderRow(fetchDepartmentRow);
  const fetchOrders = useFetchOrders(row.fetchOrderRow, onHiresChanged);
  const setSuccessMessage = useSuccessToast();
  const lineActions = useHireLineActions({ ...tableProps, allItems: tableProps.list && tableProps.list.allItems });
  const exp = useDeptOrderExpansion({
    setOrders: row.setOrders,
    fetchOrders,
    isTableLayout: true,
    setSuccessMessage,
    onListRefresh: fetchOrders,
    deferReferenceData: true,
    hireActions: lineActions.hireActions,
    // Every mutation in the panel -> the page's SILENT hires refetch, so tabs,
    // counts and the paperclip follow at once (2026-09-29, owner's feedback).
    onOrderMutated: onHiresChanged,
  });
  useDetailFollowsHireRows(exp, tableProps.list && tableProps.list.allItems, exp.editDirtyId !== null);
  // This hook OWNS its dirty id; the wrapper's leave guard reads a mirror.
  useEffect(() => {
    onDirtyChange(exp.editDirtyId);
  }, [exp.editDirtyId, onDirtyChange]);
  useEffect(() => () => onDirtyChange(null), [onDirtyChange]);
  const expansion = useHireRowExpansion({ exp, editDirtyId: exp.editDirtyId, ...row, ...rowRequest });
  return (
    <>
      <HireTableView {...tableProps} expansion={expansion} />
      {exp.dialogs}
      <HireBulkDialogs
        lineActions={lineActions}
        refreshOrderDetail={exp.refreshOrderDetail}
        fetchOrders={fetchOrders}
        setSuccessMessage={setSuccessMessage}
      />
    </>
  );
}

/** My Hires for a viewer who sees only their own hires — My Orders' surface. */
function OwnHireOrderTable({ onHiresChanged, editDirtyId, setEditDirtyId, rowRequest, tableProps }) {
  const currentUser = localStorage.getItem('sfx_requested_by') || '';
  const currentUserId = localStorage.getItem('sfx_user_id') || '';
  const fetchOwnRows = useCallback(async () => {
    const res = await apiGet(`${API_BASE}/api/orders?user_id=${currentUserId}`);
    if (!res.ok) throw new Error('Failed to fetch orders');
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }, [currentUserId]);
  const row = useExpandedOrderRow(fetchOwnRows);
  const fetchOrders = useFetchOrders(row.fetchOrderRow, onHiresChanged);
  const setSuccessMessage = useSuccessToast();
  const lineActions = useHireLineActions({ ...tableProps, allItems: tableProps.list && tableProps.list.allItems });
  const exp = useMyOrderExpansion({
    setOrders: row.setOrders,
    fetchOrders,
    editDirtyId,
    setEditDirtyId,
    currentUser,
    currentUserId,
    onListRefresh: fetchOrders,
    deferReferenceData: true,
    hireActions: lineActions.hireActions,
    // Every mutation in the panel -> the page's SILENT hires refetch, so tabs,
    // counts and the paperclip follow at once (2026-09-29, owner's feedback).
    onOrderMutated: onHiresChanged,
  });
  useDetailFollowsHireRows(exp, tableProps.list && tableProps.list.allItems, editDirtyId !== null);
  useEffect(() => () => setEditDirtyId(null), [setEditDirtyId]);
  const expansion = useHireRowExpansion({ exp, editDirtyId, ...row, ...rowRequest });
  return (
    <>
      <HireTableView {...tableProps} expansion={expansion} />
      {exp.dialogs}
      <HireBulkDialogs
        lineActions={lineActions}
        refreshOrderDetail={exp.refreshOrderDetail}
        fetchOrders={fetchOrders}
        setSuccessMessage={setSuccessMessage}
      />
    </>
  );
}

/**
 * THE hires table with order expansion — what both hires pages render.
 *
 * @param {boolean} deptWide  `true` → the Department Orders surface (Department
 *   Hires always; My Hires when `canViewDeptWide`); `false` → My Orders'.
 * @param {() => Promise} onHiresChanged  the page's SILENT hires refetch.
 * Every other prop goes to `HireTableView` unchanged.
 *
 * 🔑 ONE leave guard for the page, keyed on the open inline editor's dirty id
 * (`editDirtyId`) — the same guard, message and department-switch
 * registration each order page carries for the same editor.
 */
export default function HireOrderExpansion({
  deptWide, onHiresChanged, expandItem = null, onExpandHandled, onDirtyItemChange, continueWhenCleanRef,
  onExpandedItemChange, ...tableProps
}) {
  // The page's hooks into the row expansion: open a line on request (the
  // `?hire=` deep link), hear which line holds a DIRTY form (its pin + poll
  // pause), a ref the page's view switch / status tabs / "View order" call to
  // ask-then-continue (`hooks/useDirtyContinuation.js`), and hear which line
  // is OPEN (`onExpandedItemChange`, optional — the page keeps it open across
  // Kanban / Gantt / Calendar, 2026-09-28).
  const rowRequest = { expandItem, onExpandHandled, onDirtyItemChange, continueWhenCleanRef, onExpandedItemChange };
  const [editDirtyId, setEditDirtyId] = useState(null);
  const { registerDirtyEditGuard } = useDepartment();
  useEffect(() => {
    registerDirtyEditGuard?.(editDirtyId !== null);
    return () => registerDirtyEditGuard?.(false);
  }, [editDirtyId, registerDirtyEditGuard]);
  const leaveGuard = useUnsavedChangesGuard({
    when: editDirtyId !== null,
    message: UNSAVED_ORDER_CHANGES_MESSAGE,
  });
  return (
    <>
      {deptWide ? (
        <DeptHireOrderTable
          key="dept"
          onHiresChanged={onHiresChanged}
          rowRequest={rowRequest}
          onDirtyChange={setEditDirtyId}
          tableProps={tableProps}
        />
      ) : (
        <OwnHireOrderTable
          key="own"
          onHiresChanged={onHiresChanged}
          rowRequest={rowRequest}
          editDirtyId={editDirtyId}
          setEditDirtyId={setEditDirtyId}
          tableProps={tableProps}
        />
      )}
      {leaveGuard.dialog}
    </>
  );
}

